<script>
import {useMedicalReasonStore} from "@/stores/Medical/MedicalReason.js"
import useSharedData from "@/use/SharedData.js";
import useSharedMethod from "@/use/SharedMethod.js";
import useTools from "@/use/Tools.js";
import {onMounted, watch} from "vue";
import {useSecurityStore} from "@/stores/Security.js";
import axios from "axios";

export default {
    setup() {
        const {total, loading, filter} = useSharedData();

        const {priceFormat, transformFilterValues} = useSharedMethod();

        const {$i18n, $router} = useTools();

        const medicalReasonStore = useMedicalReasonStore();
        const securityStore = useSecurityStore();

        const headers = [
            {
                text: "...",
                align: "left",
                sortable: false,
                value: "action",
                width: "0%"
            },
            {
                text: $i18n.t("common.code"),
                align: "left",
                sortable: true,
                value: "code",
                width: "0%"
            },
            {
                text: $i18n.t("common.label"),
                align: "left",
                sortable: true,
                value: "name",
                width: "0%"
            },
            {
                text: $i18n.t("common.duration"),
                align: "left",
                sortable: true,
                value: "duration",
                width: "0%"
            },
            {
                text: $i18n.t("common.periodicity"),
                align: "left",
                sortable: true,
                value: "periodicity",
            },
        ];

        const actions = [
            {
                title: $i18n.t("common.edit"),
                icon: 'mdi-pencil-box-multiple',
                color: 'orange',
                access: () => true,
                disabled: () => false,
                func: (item) => {
                    $router.push({name: 'medical_reasons.edit', params: {id: item.id}})
                },
            },
            {
                title: $i18n.t("common.delete"),
                icon: 'mdi-trash-can',
                color: 'red',
                access: () => true,
                disabled: () => false,
                func: (item) => {
                    $router.push({name: 'medical_reasons.delete', params: {id: item.id}})
                },
            },
        ];

        function findAll() {
            loading.value = true;
            axios.get('/medicalReasons', {params: transformFilterValues(filter.value)}).then((response) => {
                medicalReasonStore.setMedicalReasons(response.data);
                total.value = response.data.length;
            }).finally(() => {
                loading.value = false;
            });
        }

        onMounted(() => findAll());

        watch(() => filter.value, () => {
            findAll();
        }, {deep: true, immediate: true});

        function view(item) {
            $router.push({name: 'medical_reasons.view', params: {id: item.id}});
        }

        return {
            medicalReasonStore,
            securityStore,
            headers,
            actions,
            total,
            loading,
            filter,
            priceFormat,
            view,
            findAll,
        };
    },
};
</script>

<template>
    <div>
        <w-app
            :title="$t('module.medical_reason')"
            :total="total"
        >
            <template v-slot:rightToolbar>
                <w-btn-reload
                    :loading="loading"
                    @press="findAll"
                />
                <w-btn-add
                    :show="true"
                    @press="$router.push({ name: 'medical_reasons.add' })"
                />
            </template>

            <template v-slot:content>
                <v-container fluid>
                    <v-row dense>
                        <v-col cols="3">
                            <w-text-field
                                v-model="filter.name"
                                :label="$t('common.label')"
                            />
                        </v-col>
                    </v-row>
                </v-container>
                <w-data-table
                    :headers="headers"
                    :actions="actions"
                    :items="medicalReasonStore.medicalReasons"
                    :total="total"
                    :loading="loading"
                    sort-by="code"
                    @rowDbClick="view"
                >

                    <template v-slot:[`item.code`]="{ item }">
                        <w-chip :text="item.code"/>
                    </template>
                    <template v-slot:[`item.name`]="{ item }">
                        <w-inline-text bold :text="item.name"/>
                        <w-btn-icon-help :text="item.observation"/>
                    </template>
                    <template v-slot:[`item.duration`]="{ item }">
                        <w-inline-text :text="item.duration"/>
                    </template>
                    <template v-slot:[`item.periodicity`]="{ item }">
                        <w-inline-text
                            v-if="item.enablePeriodicity"
                            :text="`${item.periodicityDuration} ${$t(`common.${item.periodicity}`)}`"
                        />
                    </template>
                    <template v-slot:[`item.observation`]="{ item }">
                        <w-inline-text :text="item.observation"/>
                    </template>
                </w-data-table>
            </template>
        </w-app>
        <router-view/>
    </div>
</template>
